<template>
  <f7-page name="group-listing-manager" @page:show="onShow">
    <f7-navbar :title="$t('groupListingManager.title')" back-link no-shadow>
      <f7-nav-right>
        <f7-link @click="refresh">
          <f7-icon f7="arrow_clockwise" size="18" />
        </f7-link>
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <f7-block v-if="!isAdmin" class="glm-denied">
          <f7-icon f7="lock_fill" size="22" />
          <p>{{ $t('groupListingManager.adminOnly') }}</p>
        </f7-block>

        <div v-else-if="loading" class="text-align-center" style="padding: 32px 0">
          <f7-preloader />
        </div>

        <template v-else>
          <f7-segmented strong round class="glm-tabs">
            <f7-button round
              :class="{ 'glm-tab-active': tab === 'PENDING' }"
              @click="tab = 'PENDING'"
            >
              {{ $t('groupListingManager.pending') }} ({{ pendingItems.length }})
            </f7-button>
            <f7-button round
              :class="{ 'glm-tab-active': tab === 'PUBLISHED' }"
              @click="tab = 'PUBLISHED'"
            >
              {{ $t('groupListingManager.published') }} ({{ publishedItems.length }})
            </f7-button>
          </f7-segmented>

          <!-- Pending requests -->
          <f7-block v-if="tab === 'PENDING'">
            <f7-list v-if="pendingRaw.length" inset>
              <f7-list-item
                v-for="gl in pendingRaw"
                :key="gl.groupListingId || gl.listing?.id"
                :title="gl.listing?.title"
                :subtitle="sellerName(gl)"
              >
                <template #media>
                  <img v-if="pendingCover(gl)" class="listing-avatar" :src="pendingCover(gl)" :alt="gl.listing?.title" />
                  <span v-else class="listing-avatar glm-avatar-ph"><f7-icon f7="tag_fill" size="16" /></span>
                </template>
                <template #after="{ }">
                  <div class="glm-after">
                    <span v-if="gl.adminNote" class="glm-note">
                      {{ $t('groupListingManager.adminNote') }}: {{ gl.adminNote }}
                    </span>
                    <span class="glm-badge glm-badge--pending">{{ $t('groupListingManager.pending') }}</span>
                    <div class="glm-actions">
                      <f7-button
                        small round
                        fill raised
                        @click="approve(gl)"
                      >
                        {{ $t('groupListingManager.approve') }}
                      </f7-button>
                      <f7-button
                        small raised round
                        outline
                        @click="openReject(gl)"
                      >
                        {{ $t('groupListingManager.reject') }}
                      </f7-button>
                      <f7-link small @click="toggleFeaturedPending(gl)">
                        <f7-icon f7="star" :class="gl.featured ? 'glm-star-on' : ''" size="15" />
                      </f7-link>
                    </div>
                  </div>
                </template>
              </f7-list-item>
            </f7-list>
            <p v-else class="text-align-center text-color-gray glm-empty">
              {{ $t('groupListingManager.noPending') }}
            </p>
          </f7-block>

          <!-- Published listings -->
          <f7-block v-else>
            <f7-list v-if="publishedItems.length" inset>
              <f7-list-item
                v-for="o in publishedItems"
                :key="o.id"
                :title="o.title"
                :subtitle="o.sellerName"
              >
                <template #media>
                  <img v-if="o.media?.[0]" class="listing-avatar" :src="o.media[0]" :alt="o.title" />
                  <span v-else class="listing-avatar glm-avatar-ph"><f7-icon f7="tag_fill" size="16" /></span>
                </template>
                <template #after="{ }">
                  <div class="glm-after">
                    <span class="glm-price">{{ formatPrice(o) }}</span>
                    <span v-if="o.featured" class="glm-badge glm-badge--featured">
                      {{ $t('groupListingManager.featured') }}
                    </span>
                    <div class="glm-actions">
                      <f7-link small @click="toggleFeaturedPublished(o)">
                        <f7-icon f7="star" :class="o.featured ? 'glm-star-on' : ''" size="15" />
                      </f7-link>
                      <f7-button
                        v-if="o.status === 'PUBLISHED'"
                        small
                        outline
                        raised
                        round
                        color="orange"
                        @click="pause(o)"
                      >
                        {{ $t('groupListingManager.pause') }}
                      </f7-button>
                      <f7-button
                        v-else
                        small round
                        fill
                        raised
                        @click="resume(o)"
                      >
                        {{ $t('groupListingManager.resume') }}
                      </f7-button>
                    </div>
                  </div>
                </template>
              </f7-list-item>
            </f7-list>
            <p v-else class="text-align-center text-color-gray glm-empty">
              {{ $t('groupListingManager.noPublished') }}
            </p>
          </f7-block>
        </template>
      </template>
    </AppLayout>

    <!-- Reject / request changes -->
    <AdaptiveSheet v-model:opened="rejectSheetOpen" swipe-to-close :tablet-fullscreen="false">
      <f7-page-content>
        <f7-block-title class="text-align-center">{{ $t('groupListingManager.rejectTitle') }}</f7-block-title>
        <f7-list inset strong no-hairlines>
          <f7-list-input
            type="textarea"
            resizable
            :label="$t('groupListingManager.reason')"
            :value="rejectNote"
            @input="rejectNote = $event.target.value"
          />
        </f7-list>
        <f7-block>
          <f7-button fill raised small @click="reject(rejectTarget)">
            {{ $t('groupListingManager.reject') }}
          </f7-button>
        </f7-block>
      </f7-page-content>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../common/AppLayout.vue";
import AdaptiveSheet from "../common/AdaptiveSheet.vue";
import { useMarketplaceStore } from "../../js/pinia-stores/marketplace.js";
import { useMarketplaceProductStore } from "../../js/pinia-stores/marketplaceProduct.js";
import { useGroupsStore } from "../../js/pinia-stores/group.js";
import { useMembersStore } from "../../js/pinia-stores/members.js";
import { useKeycloakStore } from "../../js/pinia-stores/keycloak.js";
import i18n from "../../js/i18n.js";

const props = defineProps({
  groupId: { type: String, required: true },
});

const { t } = i18n.global;
const marketplaceStore = useMarketplaceStore();
const mpStore = useMarketplaceProductStore();
const groupsStore = useGroupsStore();
const membersStore = useMembersStore();
const keycloakStore = useKeycloakStore();

const loading = ref(true);
const tab = ref("PENDING");

const rejectSheetOpen = ref(false);
const rejectTarget = ref(null);
const rejectNote = ref("");

const currentUserId = computed(() => keycloakStore.subject || "");

const isAdmin = computed(() => {
  const viewer = membersStore.members.find(
    (m) => m.userId === currentUserId.value,
  );
  return viewer?.role === "OWNER" || viewer?.role === "ADMIN";
});

const pendingRaw = computed(() =>
  marketplaceStore.pendingCache[props.groupId]
    ? marketplaceStore.pendingCache[props.groupId]
    : [],
);

const pendingItems = computed(() => marketplaceStore.pending(props.groupId));

const publishedItems = computed(() =>
  marketplaceStore.published(props.groupId),
);

function sellerName(gl) {
  return gl.seller?.name || gl.submittedByName || "—";
}

function pendingCover(gl) {
  const m = gl.listing?.media?.[0];
  return m?.thumbnailUrl || m?.url || (typeof m === "string" ? m : "");
}

function formatPrice(o) {
  if (o.price == null || o.price === "") return t("marketplace.priceOnRequest");
  const n = Number(o.price);
  return `${n.toLocaleString()}${o.currency ? " " + o.currency : ""}`;
}

async function refresh() {
  loading.value = true;
  try {
    await marketplaceStore.refreshAll(props.groupId);
  } finally {
    loading.value = false;
  }
}

async function onShow() {
  try {
    if (!membersStore.members.length) {
      await membersStore.fetchMembers(props.groupId).catch(() => {});
    }
    if (!groupsStore.activeGroup) {
      await groupsStore.fetchGroupDetail(props.groupId).catch(() => {});
    }
  } catch (e) {
    // fall through
  }
  await refresh();
}

async function approve(gl) {
  try {
    const listingId = gl.listing?.id;
    if (!listingId) return;
    await marketplaceStore.approveOffer(listingId, props.groupId, {
      inDigest: !!gl.featured,
    });
    setTimeout(() => refresh(), 300);
    f7.toast.show({
      text: t("groupListingManager.approved"),
      closeTimeout: 2000,
    });
  } catch (e) {
    f7.dialog.alert(e?.message || t("groupListingManager.error"), t("common.error"));
  }
}

async function toggleFeaturedPending(gl) {
  try {
    const listingId = gl.listing?.id;
    if (!listingId) return;
    await marketplaceStore.approveOffer(listingId, props.groupId, {
      inDigest: !gl.featured,
    });
    setTimeout(() => refresh(), 300);
  } catch (e) {
    f7.dialog.alert(e?.message || t("groupListingManager.error"), t("common.error"));
  }
}

function openReject(gl) {
  rejectTarget.value = gl;
  rejectNote.value = "";
  rejectSheetOpen.value = true;
}

async function reject(gl) {
  if (!gl) return;
  rejectSheetOpen.value = false;
  const listingId = gl.listing?.id;
  if (!listingId) return;
  try {
    await marketplaceStore.rejectOffer(listingId, props.groupId, rejectNote.value.trim());
    setTimeout(() => refresh(), 300);
    f7.toast.show({ text: t("groupListingManager.rejected"), closeTimeout: 2000 });
  } catch (e) {
    f7.dialog.alert(e?.message || t("groupListingManager.error"), t("common.error"));
  }
}

async function toggleFeaturedPublished(o) {
  try {
    await marketplaceStore.toggleFeatured(o.id, props.groupId);
    setTimeout(() => refresh(), 300);
  } catch (e) {
    f7.dialog.alert(e?.message || t("groupListingManager.error"), t("common.error"));
  }
}

async function pause(o) {
  f7.dialog.confirm(
    t("groupListingManager.pauseConfirm", { title: o.title }),
    t("groupListingManager.pause"),
    async () => {
      try {
        if (o.groupListingId) {
          await mpStore.updateGroupMarketplaceListing(props.groupId, o.groupListingId, {
            status: "REMOVED",
          });
        } else {
          await marketplaceStore.reviewSubmission(
            props.groupId,
            o.id,
            { status: "REMOVED", adminNote: "", featured: false },
          );
        }
        await refresh();
        f7.toast.show({ text: t("groupListingManager.paused"), closeTimeout: 2000 });
      } catch (e) {
        f7.dialog.alert(e?.message || t("groupListingManager.error"), t("common.error"));
      }
    },
  );
}

async function resume(o) {
  try {
    if (o.groupListingId) {
      await mpStore.updateGroupMarketplaceListing(props.groupId, o.groupListingId, {
        status: "APPROVED",
      });
    } else {
      await marketplaceStore.reviewSubmission(
        props.groupId,
        o.id,
        { status: "APPROVED", adminNote: "", featured: false },
      );
    }
    await refresh();
    f7.toast.show({ text: t("groupListingManager.resumed"), closeTimeout: 2000 });
  } catch (e) {
    f7.dialog.alert(e?.message || t("groupListingManager.error"), t("common.error"));
  }
}

onMounted(() => {
  onShow();
});
</script>

<style scoped>
.glm-denied {
  text-align: center;
  padding: 40px 16px;
  color: var(--f7-text-color, #333);
}
.glm-tabs {
  margin: 8px 16px 0;
}
.glm-tab-active {
  background: color-mix(in srgb, var(--f7-theme-color) 14%, transparent);
}
.glm-after {
  text-align: right;
}
.glm-price {
  font-size: 13px;
  font-weight: 600;
  color: var(--avocash-green, #1a6b42);
  display: block;
}
.glm-note {
  display: block;
  font-size: 11px;
  color: color-mix(in srgb, #b25e09 90%, transparent);
  margin-bottom: 3px;
}
.glm-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  margin-bottom: 4px;
}
.glm-badge--pending { background: color-mix(in srgb, #b25e09 12%, transparent); color: #b25e09; }
.glm-badge--featured { background: color-mix(in srgb, #b8860b 14%, transparent); color: #b8860b; }
.glm-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  margin-top: 6px;
}
.glm-star-on {
  color: #b8860b;
}
.glm-avatar-ph {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--f7-theme-color);
}
.glm-empty {
  padding: 16px 0 32px;
}
</style>